import React, { useEffect, useState } from "react";
import FHIR from "fhirclient";
import { useParams } from "react-router-dom";
import {
  Anchor,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Image,
  Modal,
  MultiSelect,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import Layout from "./Layout";
import { hospitalNames, referralSections, specialties } from "./Constant";
import { PatientDetails } from "../questionnare/Card";
import { getPatientName } from "../Eligibility/EligibiltyService";
import { showNotification } from "@mantine/notifications";
import { useMedplum } from "@medplum/react";
import { DateInput } from "@mantine/dates";
import hospitalRequest from "../../assets/img/hospitalRequest.gif";

import {
  IconCalendar,
  IconMail,
  IconProgressCheck,
  IconWorld,
} from "@tabler/icons-react";
import BorderFrame from "./Border";
import Footer from "./EmbedFooter";
import { extractNoteTexts } from "./Service";

interface HealthCheckAppProps {
  aiName?: string;
}
interface Observation {
  resource: {
    code: { text: string };
    valueQuantity?: { value: number; unit: string };
  };
}

const Referral: React.FC<HealthCheckAppProps> = ({ aiName }) => {
  const { code } = useParams();
  const medplum = useMedplum();
  const [patient, setPatient] = useState<any>();
  const [showBorder, setShowBorder] = useState<boolean>(false);
  const [conditions, setConditions] = useState<any>([]);
  const [observations, setObservations] = useState<any>([]);
    const [procedures, setProcedures] = useState<any>([]);
      const [selectedSymptom, setSelectedSymptoms] = useState<
        string | undefined
      >();



  const [login, setLogin] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);

  // State to store form data
  const [formData, setFormData] = useState({
    referringPhysician: "Dr. America Turner",
    dateOfReferral: "",
    primaryDiagnosis: "",
    secondaryConditions: "",
    urgencyLevel: "",
    currentMedications: "",
    allergies: "",
    previousTreatments: "",
    immunizationStatus: "",
    labResults: "",
    additionalNotes: "",
    referredSpecialty: [] as string[],
    preferredClinic: "",
    followUpInstructions: "",
    hasSelfPay: "",
    selfPayComments: "",
    hasInsurance: "",
    insuranceComments: "",
    referralReason: "",
    acidentRelated: "",
    accidentRelatedComments:""
  });

  const handleCheckboxChange = (isChecked: any) => {
    setShowBorder(isChecked);
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        const client = await FHIR.oauth2.ready();

        // Get the patient data
        const patientData = await client.patient.read<any>();
        setPatient(patientData);
        const conditionsResponse = await client.request<{ entry: any }>(
          `Condition?patient=${client.patient.id}&_count=20`
        );
        const symptoms = extractNoteTexts(conditionsResponse.entry);
        setFormData((prevState) => ({
          ...prevState,
          allergies: symptoms,
        }));
        setSelectedSymptoms(symptoms);

        setConditions(conditionsResponse.entry || []);
        const observationsResponse = await client.request<{
          entry: Observation[];
        }>(`Observation?patient=${client.patient.id}&_sort=-date&_count=2`);
        const codeTexts = observationsResponse.entry.map(
          (obs: any) => obs.resource.code.text
        );
        const codeTextDisplay = codeTexts.join(", ");
        
        setFormData((prevState) => ({
          ...prevState,
          labResults: codeTextDisplay,
        }));

        setObservations(observationsResponse.entry || []);

        const procedureResponse = await client.request<{
          entry: Observation[];
        }>(`Procedure?patient=${client.patient.id}&_sort=-date&_count=2`);
        setProcedures(procedureResponse.entry || []);
        const textValues = procedureResponse.entry.map(
          (observation: any) => observation.resource.code.text
        );
        setFormData((prevState) => ({
          ...prevState,
          previousTreatments: textValues[0],
        }));

        const immunizationResponse = await client.request<{
          entry: Observation[];
        }>(`Immunization?patient=${client.patient.id}&_sort=-date&_count=2`);
        const vaccineCodes = immunizationResponse.entry.map(
          (immunization: any) => immunization.resource.vaccineCode.text
        );

        setFormData((prevState) => ({
          ...prevState,
          immunizationStatus: vaccineCodes as any,
        }));

        const medicationResponse = await client.request<{
          entry: Observation[];
        }>(
          `MedicationRequest?patient=${client.patient.id}&_sort=-date&_count=2`
        );
        const medicationTexts = medicationResponse.entry.map(
          (request: any) => request.resource.medicationCodeableConcept.text
        );
        const resultString = medicationTexts.join(", ");

          setFormData((prevState) => ({
            ...prevState,
            currentMedications: resultString,
          }));
      } catch (error) {
        showNotification({
          color: "red",
          message: `Error Fetching data: ${error}`,
          autoClose: true,
        });
        console.error("Error fetching FHIR data", error);
      }
    };
    fetchData();
    loginWithIdandSecret();
  }, []);
 
   useEffect(() => {
     if (showModal) {
       // Show the loading message for 5 seconds
       const timer = setTimeout(() => {
         setLoading(false);
       }, 5000);

       // Cleanup the timeout when the modal is closed
       return () => clearTimeout(timer);
     }
   }, [showModal]);
  

  const loginWithIdandSecret = async () => {
    try {
      const decodedQuery = decodeURIComponent(code as string);

      const decodeQueryStringWithData = atob(decodedQuery);

      const queryParamss = decodeQueryStringWithData.split(",");
      //   setClientData(queryParamss);
      const id = queryParamss[0];
      const secret = queryParamss[1];

      const login = await medplum.startClientLogin(id, secret);
      if (login) {
        setLogin(true);
        return true;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = () => {
    setLoading(true)
    setShowModal(true);
  };
  const isFormValid = () => {
    const {
      referringPhysician,
      dateOfReferral,
      primaryDiagnosis,
      secondaryConditions,
      urgencyLevel,
      currentMedications,
      allergies,
      previousTreatments,
      immunizationStatus,
      labResults,
      additionalNotes,
      referredSpecialty,
      preferredClinic,
      followUpInstructions,
      hasSelfPay,
      selfPayComments,
      hasInsurance,
      insuranceComments,
      referralReason,
      acidentRelated,
      accidentRelatedComments,
    } = formData;

    return (
      referringPhysician !== "" &&
      dateOfReferral !== "" &&
      urgencyLevel !== "" &&
      currentMedications !== "" &&
      allergies !== "" &&
      previousTreatments !== "" &&
      immunizationStatus !== "" &&
      labResults !== "" &&
      additionalNotes !== "" &&
      referredSpecialty.length > 0 &&
      preferredClinic !== "" &&
      followUpInstructions !== "" &&
      hasSelfPay !== "" &&
      selfPayComments !== "" &&
      hasInsurance !== "" &&
      insuranceComments !== "" &&
      referralReason !== "" &&
      accidentRelatedComments !== "" &&
      acidentRelated!==""
    );
  };
  

  return (
    <BorderFrame showBorder={showBorder}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          backgroundColor: "#F8F8F8",
          height: showBorder ? "calc(100vh - 75px)" : "calc(100vh - 75px)", // Adjusted height
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            height: showBorder
              ? "calc(100vh - 80px)"
              : "aucalc(100vh - 75px)to", // Adjusted height
            // backgroundColor: "red",
          }}
        >
          <Box>
            <Layout
              showBorderFunction={handleCheckboxChange}
              title="Referral Workflow Manager"
              data={referralSections}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              padding: "8px",
              height: showBorder ? "calc(100vh - 75px)" : "calc(100vh - 140px)", // Adjusted height
            }}
          >
            <Box sx={{ backgroundColor: "white", borderRadius: "8px" }}>
              <PatientDetails
                name={getPatientName(patient?.name)}
                dob={patient?.birthDate ?? "--"}
                mobile={patient?.telecom?.[0]?.value}
                address={"--"}
                email={patient?.email ?? "--"}
                gender={patient?.gender ?? "--"}
                moreDetails={true}
                city={patient?.address?.[0]?.city}
                country={patient?.address?.[0]?.country}
                age={
                  patient?.birthDate
                    ? new Date().getFullYear() -
                      new Date(patient.birthDate).getFullYear() -
                      (new Date().setFullYear(0) <
                      new Date(patient.birthDate).setFullYear(0)
                        ? 1
                        : 0)
                    : "--"
                }
              />
            </Box>
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: showBorder ? "100vh" : "65vh",
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": {
                  width: "1px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#555",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
              }}
            >
              <Box>
                {/* Referral Information Section */}
                <Box
                  sx={{
                    borderRadius: "8px",
                    marginBottom: "20px",
                    backgroundColor: "white",
                    padding: "12px",
                  }}
                >
                  <Title order={3} mb={"8px"}>
                    Referral Information
                  </Title>
                  <Grid>
                    <Grid.Col span={6}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label="Referring Physician "
                        placeholder="Enter details"
                        value={formData.referringPhysician}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            referringPhysician: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <DateInput
                        size="md"
                        value={formData.dateOfReferral as any}
                        onChange={(date) =>
                          setFormData({
                            ...formData,
                            dateOfReferral: date as any,
                          })
                        }
                        radius={"md"}
                        label="Date of Referral"
                        placeholder="Enter date"
                        rightSection={<IconCalendar size={"20px"} />}
                      />
                    </Grid.Col>

                    <Grid.Col span={12}>
                      <Select
                        size="md"
                        label="Urgency Level"
                        placeholder="Select urgency"
                        data={["Routine", "Urgent", "STAT(Same Day)"]}
                        value={formData.urgencyLevel}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            urgencyLevel: value ?? "",
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Select
                        size="md"
                        label="Authorization for referral needed?
"
                        placeholder="Select answer"
                        data={["Yes", "No"]}
                        value={formData.hasInsurance}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            hasInsurance: value ?? "",
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label="Comments "
                        placeholder="Enter comments"
                        value={formData.insuranceComments}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            insuranceComments: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <Select
                        size="md"
                        label="Does patient have insurance or self-pay?
"
                        placeholder="Select answer"
                        data={["Yes", "No"]}
                        value={formData.hasSelfPay}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            hasSelfPay: value ?? "",
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label="Comments"
                        placeholder="Enter comments"
                        value={formData.selfPayComments}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            selfPayComments: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <Select
                        size="md"
                        label="Is the referral accident related?"
                        placeholder="Select answer"
                        data={["Yes", "No"]}
                        value={formData.acidentRelated}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            acidentRelated: value ?? "",
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label="Comments"
                        placeholder="Enter comments"
                        value={formData.accidentRelatedComments}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            accidentRelatedComments: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                  </Grid>
                </Box>

                {/* Medical History Section */}
                <Box
                  sx={{
                    borderRadius: "8px",
                    marginBottom: "20px",
                    backgroundColor: "white",
                    padding: "12px",
                  }}
                >
                  <Title order={3} mb={"8px"}>
                    Medical History
                  </Title>
                  <Grid>
                    <Grid.Col span={12}>
                      <TextInput
                        size="md"
                        label="Current Medications"
                        placeholder="Enter medications"
                        value={formData.currentMedications}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            currentMedications: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        size="md"
                        label="Primary Conditions"
                        placeholder="Enter Primary Conditions"
                        value={formData.allergies}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            allergies: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        label="Previous Treatments/Procedures"
                        placeholder="Enter treatments"
                        value={formData.previousTreatments}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            previousTreatments: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label="Immunization Status"
                        placeholder="Enter immunization status"
                        value={formData.immunizationStatus}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            immunizationStatus: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        radius={"md"}
                        size="md"
                        label="Observations"
                        placeholder="Enter observations"
                        value={formData.labResults}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            labResults: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label="Additional Notes (Behavioral, Developmental concerns)"
                        placeholder="Enter notes"
                        value={formData.additionalNotes}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            additionalNotes: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                  </Grid>
                </Box>

                {/* Referral Destination Section */}
                <Box
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "white",
                    padding: "12px",
                  }}
                >
                  <Title order={3} mb={"8px"}>
                    Referral Destination
                  </Title>
                  <Grid>
                    <Grid.Col span={12}>
                      {/* <TextInput
                        size="md"
                        radius={"md"}
                        label="Referred Specialty (e.g., Cardiology, Neurology, etc.)"
                        placeholder="Enter specialty"
                        value={formData.referredSpecialty}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            referredSpecialty: e.target.value,
                          })
                        }
                      /> */}
                      <MultiSelect
                        data={specialties}
                        label="Referred Specialty"
                        placeholder="Select specialty"
                        value={formData.referredSpecialty}
                        onChange={(values) =>
                          setFormData({
                            ...formData,
                            referredSpecialty: values,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label=" Referral Reason"
                        placeholder="Enter Referral Reason"
                        value={formData.referralReason}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            referralReason: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Select
                        data={hospitalNames}
                        size="md"
                        radius={"md"}
                        label="Preferred Clinic or Specialist"
                        placeholder="Enter clinic or specialist"
                        value={formData.preferredClinic}
                        onChange={(values) =>
                          setFormData({
                            ...formData,
                            preferredClinic: values as string,
                          })
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        size="md"
                        radius={"md"}
                        label="Follow-up Instructions"
                        placeholder="Enter follow-up instructions"
                        value={formData.followUpInstructions}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            followUpInstructions: e.target.value,
                          })
                        }
                      />
                    </Grid.Col>
                  </Grid>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                padding: "8px",
              }}
            >
              <Button onClick={handleSubmit} disabled={!isFormValid()}>
                Refer to{" "}
                {formData.preferredClinic
                  ? formData.preferredClinic.charAt(0).toUpperCase() +
                    formData.preferredClinic.slice(1).toLowerCase()
                  : "hospital"}
              </Button>
            </Box>
          </Box>
        </Box>
        <Footer />
        {showModal && (
          <Modal
            opened={showModal}
            onClose={() => setShowModal(false)}
            size={"lg"}
            withCloseButton={false}
            centered
            closeOnClickOutside={true}
            closeOnEscape={false}
          >
            <Flex
              mih={250}
              justify="center"
              align="center"
              direction={"column"}
              gap={"20px"}
            >
              {loading ? (
                <>
                  <Box w={"250px"}>
                    {" "}
                    <Image src={hospitalRequest} />
                  </Box>
                  <Title fw={600} lh="30px" size="lg">
                    Please wait, we are sending the request to{" "}
                    {formData.preferredClinic.charAt(0).toUpperCase() +
                      formData.preferredClinic.slice(1).toLowerCase() ??
                      "clinic"}
                  </Title>
                </>
              ) : (
                <>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconProgressCheck size={"60px"} color="green" />
                  </Box>
                  <Title fw={600} lh={"30px"} size="lg">
                    Patient has been successfully referred to{" "}
                    {formData.preferredClinic.charAt(0).toUpperCase() +
                      formData.preferredClinic.slice(1).toLowerCase() ??
                      "clinic"}
                  </Title>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      onClick={() => {
                        // Optionally reset the form here
                        setFormData({
                          referringPhysician: "",
                          dateOfReferral: "",
                          primaryDiagnosis: "",
                          secondaryConditions: "",
                          urgencyLevel: "",
                          currentMedications: "",
                          allergies: "",
                          previousTreatments: "",
                          immunizationStatus: "",
                          labResults: "",
                          additionalNotes: "",
                          referredSpecialty: [],
                          preferredClinic: "",
                          followUpInstructions: "",
                          hasSelfPay: "",
                          selfPayComments: "",
                          hasInsurance: "",
                          insuranceComments: "",
                          referralReason: "",
                          acidentRelated: "",
                          accidentRelatedComments: "",
                        });
                        setShowModal(false);
                      }}
                    >
                      Create Other Referral
                    </Button>
                  </Box>
                </>
              )}
            </Flex>
          </Modal>
        )}
      </Box>
    </BorderFrame>
  );
};

export default Referral;
